import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Container } from "@mui/material";
import UnauthorizedAccess from "components/RequirePermission/UnauthorizedAccess";
import { useOrganizationCapabilities } from "components/RequirePermission/useCapabilities";
import { useGetOrgByIdQuery } from "state/rtkQuery/org/orgQueries";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import { useGetUsersByOrgAndStatusQuery } from "state/rtkQuery/user/userQueries";

import JoinedTab from "./components/JoinedTab/JoinedTab";
import RejectedTab from "./components/RejectedTab/RejectedTab";
import RemovedTab from "./components/RemovedTab/RemovedTab";
import RemoveRequestedTab from "./components/RemoveRequestedTab/RemoveRequestedTab";
import RequestedTab from "./components/RequestedTab/RequestedTab";
import SideNav from "./components/SideNav/SideNav";
import {
  userJoinedHistoryMockList2,
  userRejectedHistoryMockList,
} from "./components/userHistoryMockList";

import "./OrgHistory.css";

const OrgHistory = () => {
  const [filter, setFilter] = useState("Joined");
  const [syncStatus, setSyncStatus] = useState("false");
  const { orgId } = useParams();

  // Joined/Requested/Remove-Requested/Rejected are all read-only user-list
  // views of this org's membership — RequireOrgRole only gates broad entry to
  // /admin/:orgId/* (Owner/Approver/Leadership of this org, or a cross-org
  // admin role), not the specific organization:id:read permission for
  // viewing this org's data. Gated once here rather than in each of the 4
  // tab files since they're only ever rendered through this page.
  const { canReadOrganizationById } = useOrganizationCapabilities(orgId);

  // Fetching platform data, joinedUserListData, and rejectedUserListData to get the count of joined and rejected users for SideNav component
  const { data: platformData } = useGetPlatformsQuery();

  // RTK Query to get org data by orgId
  const {
    data: orgData,
    isLoading: isOrgDataLoading,
    isSuccess: isOrgDataSuccess,
    error: orgDataError,
  } = useGetOrgByIdQuery(orgId);

  const { data: joinedUserListData, refetch: refetchJoined } =
    useGetUsersByOrgAndStatusQuery(
      {
        orgId: orgId,
        status: "Joined",
        platformId: platformData?._id,
        page: 1,
        perPage: 25,
      },
      { skip: !platformData?._id, refetchOnMountOrArgChange: true }
    );

  const { data: rejectedUserListData } = useGetUsersByOrgAndStatusQuery(
    {
      orgId: orgId,
      status: "Rejected",
      platformId: platformData?._id,
      page: 1,
      perPage: 25,
    },
    { skip: !platformData?._id, refetchOnMountOrArgChange: true }
  );

  const { data: removeRequestedUserListData } = useGetUsersByOrgAndStatusQuery(
    {
      orgId: orgId,
      status: "Remove-Requested",
      platformId: platformData?._id,
      page: 1,
      perPage: 25,
    },
    { skip: !platformData?._id, refetchOnMountOrArgChange: true }
  );

  const { data: removedUserListData, refetch: refetchRemoved } =
    useGetUsersByOrgAndStatusQuery(
      {
        orgId: orgId,
        status: "Removed",
        platformId: platformData?._id,
        page: 1,
        perPage: 25,
      },
      { skip: !platformData?._id, refetchOnMountOrArgChange: true }
    );

  const {
    data: requestedUserListData,
    refetch: refetchRequested,
    isLoading: isRequestedUserListLoading,
  } = useGetUsersByOrgAndStatusQuery(
    {
      orgId: orgId,
      status: "Requested",
      platformId: platformData?._id,
      page: 1,
      perPage: 25,
    },
    { skip: !platformData?._id, refetchOnMountOrArgChange: true }
  );

  // // effect to refetch data when syncStatus changes
  // useEffect(() => {
  //   if (syncStatus && filter === "Requested") {
  //     refetchRequested();
  //     refetchJoined();
  //     refetchRemoved();
  //     setSyncStatus(false);
  //   }
  // }, [syncStatus, refetchRequested, refetchJoined, refetchRemoved]);

  if (!canReadOrganizationById) {
    return (
      <UnauthorizedAccess message="You do not have permission to view this organization's history." />
    );
  }

  return (
    <Container
      sx={{
        maxWidth: "1700px !important",
        flex: "1",
        marginTop: "3rem",
        marginBottom: "3rem",
      }}
    >
      <Box
        sx={{
          height: 420,
          display: "grid",
          gap: "10px",
          gridTemplateColumns: "0.55fr 3.5fr",
          margin: "auto",
        }}
      >
        <SideNav
          filter={filter}
          setFilter={setFilter}
          joinedCount={joinedUserListData?.total}
          rejectedCount={rejectedUserListData?.total}
          removeRequestedCount={removeRequestedUserListData?.total}
          removedCount={removedUserListData?.total}
          requestedCount={requestedUserListData?.total}
        />
        {filter === "Joined" ? (
          <JoinedTab
            orgName={orgData?.name}
            userJoinedHistoryMockList={userJoinedHistoryMockList2}
          />
        ) : filter === "Rejected" ? (
          <RejectedTab
            orgName={orgData?.name}
            userRejectedHistoryMockList={userRejectedHistoryMockList}
          />
        ) : filter === "Remove-Requested" ? (
          <RemoveRequestedTab orgName={orgData?.name} />
        ) : filter === "Removed" ? (
          <RemovedTab orgName={orgData?.name} />
        ) : filter === "Requested" ? (
          <RequestedTab
            orgName={orgData?.name}
            syncStatus={syncStatus}
            setSyncStatus={setSyncStatus}
            requestedUserListData={requestedUserListData}
            loading={isRequestedUserListLoading}
          />
        ) : null}

        {/* Main Container for Search and Table */}
      </Box>
    </Container>
  );
};

export default OrgHistory;
